/*
 * oos-sort.js  —  UrbanWearOutsiders
 * Purpose: On collection / product-list pages, visually move sold-out product
 * cards to the END of each product grid, preserving the relative order of the
 * in-stock cards and of the sold-out cards among themselves.
 *
 * SAFE BY DESIGN:
 *   - Runs only on /collections* pages (or where a known grid exists).
 *   - 100% defensive: if nothing is found it does nothing and throws nothing.
 *   - Idempotent: re-running never disturbs an already-correct order.
 *   - Vanilla JS, zero dependencies. Visual reorder only (no data changes).
 *
 * To revert: delete this asset file and remove the include line in
 * layout/theme.liquid (between the OOS-SORT START/END comments).
 */
(function () {
  "use strict";

  // Grid containers used by the Ella theme collection templates.
  var GRID_SELECTORS = [
    "#main-collection-product-grid",
    "ul.productListing",
    ".productGrid"
  ];

  // A product card counts as sold-out ONLY when it renders a product-level
  // sold-out signal: the sold-out badge or the sold-out price class (the price
  // shows "Sold out" instead of an amount). In-stock cards on this theme render
  // neither. We deliberately do NOT fall back to matching the text "Sold out",
  // because a purchasable product can contain that text in a size/variant
  // <option> (e.g. one size sold out) while still being for sale — using text
  // would wrongly demote buyable products to the end.
  function isSoldOut(card) {
    try {
      if (!card || !card.querySelector) return false;
      return !!card.querySelector(".sold-out-badge, .price--sold-out");
    } catch (e) {
      return false;
    }
  }

  // Return the direct product-card children of a grid, in document order.
  function getCards(grid) {
    var cards = [];
    try {
      var kids = grid.children;
      if (!kids) return cards;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el.nodeType !== 1) continue;
        // Card = direct <li>/<div> that contains a product-item.
        if (el.classList && (el.classList.contains("product") || el.classList.contains("product-item"))) {
          cards.push(el);
        } else if (el.querySelector && el.querySelector(".product-item")) {
          cards.push(el);
        }
      }
    } catch (e) {}
    return cards;
  }

  function sortGrid(grid) {
    try {
      if (!grid) return;
      var cards = getCards(grid);
      if (cards.length < 2) return;

      var soldOut = [];
      var lastInStockIndex = -1;
      for (var i = 0; i < cards.length; i++) {
        if (isSoldOut(cards[i])) {
          soldOut.push(cards[i]);
        } else {
          lastInStockIndex = i;
        }
      }
      if (soldOut.length === 0) return;

      // Idempotency guard: if every sold-out card already sits after the last
      // in-stock card, the grid is already ordered — do nothing (prevents
      // needless DOM writes and observer loops).
      var alreadyOrdered = true;
      for (var j = 0; j < cards.length; j++) {
        if (isSoldOut(cards[j]) && j < lastInStockIndex) { alreadyOrdered = false; break; }
      }
      if (alreadyOrdered) return;

      // Append sold-out cards to the end in their existing relative order.
      // appendChild moves the node (no clone), preserving listeners/state.
      for (var k = 0; k < soldOut.length; k++) {
        grid.appendChild(soldOut[k]);
      }
    } catch (e) {
      // Never break the storefront.
    }
  }

  function run() {
    try {
      // Only act on collection/list pages, or wherever a known grid exists.
      var onCollections = (window.location && window.location.pathname &&
        window.location.pathname.indexOf("/collections") !== -1);
      var grids = [];
      for (var s = 0; s < GRID_SELECTORS.length; s++) {
        var found = document.querySelectorAll(GRID_SELECTORS[s]);
        for (var i = 0; i < found.length; i++) {
          if (grids.indexOf(found[i]) === -1) grids.push(found[i]);
        }
      }
      if (!onCollections && grids.length === 0) return;
      for (var g = 0; g < grids.length; g++) sortGrid(grids[g]);
    } catch (e) {}
  }

  // Debounced re-run. A short timer collapses bursts of mutations (e.g. hover
  // classes, lazy images) into a single cheap pass. run() is a no-op when the
  // grid is already ordered, so frequent scheduling costs almost nothing.
  var timer = null;
  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; run(); }, 150);
  }

  function init() {
    run();
    try {
      if (window.MutationObserver) {
        var target = document.querySelector("#main-collection-product-grid") ||
                     document.querySelector("main") || document.body;
        if (target) {
          // Watch child add/remove AND class changes: the theme's variant/price
          // scripts flip a card's sold-out class (or badge) asynchronously after
          // our first pass, which must trigger a re-sort. The idempotency guard
          // in sortGrid prevents any loop and skips work when already ordered.
          var obs = new MutationObserver(function () { schedule(); });
          obs.observe(target, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class"]
          });
        }
      }
    } catch (e) {}

    // Delayed re-runs decisively win races with other theme scripts (lazy load,
    // shop_events, variant/price re-render) that insert, reposition, or flip a
    // card's availability AFTER our first pass. Each run is idempotent + null-safe.
    try {
      var delays = [300, 800, 1500, 3000, 5000, 8000];
      for (var d = 0; d < delays.length; d++) setTimeout(run, delays[d]);
    } catch (e) {}
    // Also run once the full page (images/late scripts) has loaded.
    try {
      if (document.readyState !== "complete") {
        window.addEventListener("load", function () { run(); });
      }
    } catch (e) {}
  }

  // Run immediately (grid may already be in the DOM) and on DOMContentLoaded.
  try {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
      run();
    } else {
      init();
    }
  } catch (e) {}
})();
